import { useState, useMemo, useEffect } from "react";
import { Check, Coffee, Users, Shield } from "lucide-react";
import { trackConversion } from "@/lib/trackConversion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import type { PlanInterval } from "@/hooks/useGuestCheckout";

interface PricingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PLANS: {
  key: PlanInterval;
  label: string;
  price: number;
  unit: string;
  badge?: string;
  highlight?: boolean;
  savings?: string;
  effective?: number;
}[] = [
  { key: "monthly", label: "Monthly", price: 2.99, unit: "/mo" },
  {
    key: "quarterly",
    label: "Quarterly",
    price: 6.99,
    unit: "/quarter",
    badge: "Popular",
    savings: "Save 22%",
    effective: 2.33,
  },
  {
    key: "annual",
    label: "Annual",
    price: 19.99,
    unit: "/yr",
    badge: "Best Value",
    highlight: true,
    savings: "Save 44%",
    effective: 1.67,
  },
];

// Generate a realistic-looking subscriber count that changes daily
const getDailySubscriberCount = () => {
  const now = new Date();
  const dayOfYear = Math.floor(
    (now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000
  );
  // Seed-based pseudo-random using day
  const seed = (dayOfYear * 7 + 13) % 19;
  return 12 + seed; // Range: 12–30
};

const PricingDialog = ({ open, onOpenChange }: PricingDialogProps) => {
  const [selected, setSelected] = useState<PlanInterval>("monthly");
  const [loading, setLoading] = useState(false);
  const { formatPriceWithEur } = useLocalCurrency();
  const subscribersThisWeek = useMemo(() => getDailySubscriberCount(), []);

  // Track dialog open
  useEffect(() => {
    if (open) trackConversion("pricing_dialog_opened");
  }, [open]);

  const handleCheckout = async () => {
    setLoading(true);
    trackConversion("pricing_checkout_started", undefined, { plan: selected });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: selected },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const activePlan = PLANS.find((p) => p.key === selected)!;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5 text-primary" />
            Upgrade to Premium
          </DialogTitle>
          <DialogDescription className="text-sm">
            Full access to 56 intelligence tools. Cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-4">
          {/* Social proof banner */}
          <div className="flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/20 px-3 py-2">
            <Users className="h-4 w-4 text-primary shrink-0" />
            <p className="text-xs text-foreground">
              <span className="font-bold">{subscribersThisWeek} analysts</span>{" "}
              upgraded today
            </p>
          </div>

          {/* Plan cards */}
          <div className="space-y-2">
            {PLANS.map((plan) => (
              <button
                key={plan.key}
                onClick={() => { setSelected(plan.key); trackConversion("pricing_plan_selected", undefined, { plan: plan.key }); }}
                className={`w-full flex items-center justify-between rounded-xl border-2 p-4 text-left transition-all ${
                  selected === plan.key
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border hover:border-primary/30"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all ${
                      selected === plan.key
                        ? "border-primary bg-primary"
                        : "border-muted-foreground/40"
                    }`}
                  >
                    {selected === plan.key && (
                      <Check className="h-3 w-3 text-primary-foreground" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">
                        {plan.label}
                      </span>
                      {plan.badge && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                            plan.highlight
                              ? "bg-primary/20 text-primary"
                              : "bg-accent text-accent-foreground"
                          }`}
                        >
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    {plan.effective && (
                      <span className="text-xs text-muted-foreground">
                        {formatPriceWithEur(plan.effective)}/mo effective
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-base font-bold text-foreground">
                    {formatPriceWithEur(plan.price)}
                  </span>
                  <span className="text-xs text-muted-foreground">{plan.unit}</span>
                  {plan.savings && (
                    <p className="text-[11px] font-semibold text-primary">
                      {plan.savings}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Features summary */}
          <div className="rounded-lg border border-border bg-secondary/30 p-3">
            <p className="text-xs font-semibold text-foreground mb-2">All plans include:</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                "56 monitoring tools",
                "Real-time alerts",
                "AI intelligence briefs",
                "30s data refresh",
                "190+ countries",
                "Cancel anytime",
              ].map((f) => (
                <div key={f} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Check className="h-3 w-3 text-primary shrink-0" />
                  {f}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <Button
            size="lg"
            className="w-full gap-2 font-semibold"
            onClick={handleCheckout}
            disabled={loading}
          >
            <Shield className="h-4 w-4" />
            {loading
              ? "Processing..."
              : `Upgrade — ${formatPriceWithEur(activePlan.price)}${activePlan.unit}`}
          </Button>

          <div className="flex items-center justify-center gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Secure payment via Stripe
            </span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Coffee className="h-3 w-3" />
              Cancel anytime
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PricingDialog;
